import styled from 'styled-components';
import { useSelector } from 'react-redux';
import JsonViewer from 'shared/JsonViewer';
import { Button, Box, Typography, Paper, Chip, useTheme, Grid } from '@mui/material';
import omit from 'lodash/omit';
import { Modal } from 'shared';
import { selectSingleEntityData } from 'selectors';
import { conditionallyRenderElement, copyToClipboard } from 'utils';

const Container = styled.div`
  overflow-y: auto;
  max-height: 85vh;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background-color: ${(props: any) => props.theme.palette.background.paper};
  padding: 16px 0;
  border-top: 1px solid ${(props: any) => props.theme.palette.divider};
  margin-top: 24px;
`;

const SectionTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 12px;
  color: ${(props: any) => props.theme.palette.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  onClose: () => void;
  open: boolean;
};

const SingleEntityModal = ({ onClose, open }: Props) => {
  const { data: singleEntity, isRequesting, error } = useSelector(selectSingleEntityData);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const renderJsonSection = (title: string, jsonData: any, chipLabel?: string) => {
    if (!jsonData || (typeof jsonData === 'object' && Object.keys(jsonData).length === 0)) {
      return null;
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(248, 250, 252, 0.8)',
          border: isDark
            ? '1px solid rgba(148, 163, 184, 0.12)'
            : '1px solid rgba(226, 232, 240, 0.6)',
          borderRadius: '12px',
          height: '100%',
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <SectionTitle variant="h6">
          {title}
          {chipLabel && (
            <Chip
              label={chipLabel}
              size="small"
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}
        </SectionTitle>
        <Box
          sx={{
            // react-json-view renders long token-like strings (ids, hashes) with no
            // break opportunities; these inherited properties let them wrap in place
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
            '& .react-json-view': {
              backgroundColor: 'transparent !important',
              padding: '0 !important',
            },
          }}
        >
          <JsonViewer
            name={false}
            src={jsonData}
            collapsed={2}
            displayDataTypes={false}
            displayObjectSize={true}
            enableClipboard={copyToClipboard}
            style={{
              fontSize: '0.875rem',
              lineHeight: 1.6,
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            }}
          />
        </Box>
      </Paper>
    );
  };

  return (
    <Modal modalWidth={1400} open={open} onClose={onClose}>
      {conditionallyRenderElement({
        isLoading: isRequesting as boolean,
        error: error?.message ? 'Failed to fetch entity' : null,
        content: (
          <Container>
            <Box
              sx={{
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                Entity ID: {singleEntity?.id || ''}
                {singleEntity?.dataType && (
                  <Chip
                    label={singleEntity.dataType}
                    size="small"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Pending Entities By Data Type and Pending Mapping Status
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 4 }}>
                {renderJsonSection(
                  'General Data',
                  omit(singleEntity, ['mosaicData', 'targetData']),
                  'Metadata',
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                {renderJsonSection('Mosaic Data', singleEntity?.mosaicData, 'Source')}
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                {renderJsonSection('Target Data', singleEntity?.targetData, 'Destination')}
              </Grid>
            </Grid>

            <ButtonContainer>
              <Button
                variant="contained"
                color="primary"
                onClick={onClose}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                }}
              >
                Close
              </Button>
            </ButtonContainer>
          </Container>
        ),
      })}
    </Modal>
  );
};

export default SingleEntityModal;
